import { useLayoutEffect, useRef, useState } from 'react';

const MARQUEE_SPEED_PX_PER_SECOND = 45;
const MIN_MARQUEE_DURATION_SECONDS = 8;

function OverflowMarquee({ children }) {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const text = String(children ?? '');
  const [marqueeState, setMarqueeState] = useState({
    isOverflowing: false,
    duration: MIN_MARQUEE_DURATION_SECONDS,
  });

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;

    if (!viewport || !content) return undefined;

    const measureOverflow = () => {
      const contentWidth = content.scrollWidth;
      const nextState = {
        isOverflowing: contentWidth > viewport.clientWidth + 1,
        duration: Math.max(
          MIN_MARQUEE_DURATION_SECONDS,
          contentWidth / MARQUEE_SPEED_PX_PER_SECOND,
        ),
      };

      setMarqueeState((currentState) => (
        currentState.isOverflowing === nextState.isOverflowing
        && currentState.duration === nextState.duration
          ? currentState
          : nextState
      ));
    };

    measureOverflow();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measureOverflow);
      observer.observe(viewport);
      observer.observe(content);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [text]);

  return (
    <span
      ref={viewportRef}
      className={`homework-marquee ${marqueeState.isOverflowing ? 'homework-marquee--overflowing' : ''}`}
      data-overflowing={marqueeState.isOverflowing ? 'true' : 'false'}
      title={marqueeState.isOverflowing ? text : undefined}
    >
      <span
        className="homework-marquee__track"
        data-marquee-copy={text}
        style={{ '--homework-marquee-duration': `${marqueeState.duration}s` }}
      >
        <span ref={contentRef} className="homework-marquee__content">{text}</span>
      </span>
    </span>
  );
}

export default OverflowMarquee;
